// covered-by: frontends/carrie/src/ask-v2/shell/__tests__/AskV2Layout.test.tsx
import { Outlet, useRouter, useRouterState } from "@tanstack/react-router";
import { useLayoutEffect } from "react";

import { AskV2Sidebar } from "./AskV2Sidebar";

import { AskPunttDesktopNotificationsWatcher } from "../../components/AskPunttDesktopNotificationsWatcher";
import { AuthenticationDialog } from "../../components/AuthenticationDialog";
import {
  AskV2AttachmentDraftProvider,
  useAskV2AttachmentDraftRoot,
} from "../transport/AskV2AttachmentDraftContext";

type PersonalChatDraftScope = "home" | `chat:${string}`;

export function withoutBasepath(pathname: string, basepath: string): string {
  const normalizedBasepath = `/${basepath}`
    .replace(/\/{2,}/g, "/")
    .replace(/\/+$/, "");
  if (
    normalizedBasepath === "" ||
    !(
      pathname === normalizedBasepath ||
      pathname.startsWith(`${normalizedBasepath}/`)
    )
  ) {
    return pathname;
  }
  return pathname.slice(normalizedBasepath.length) || "/";
}

export function personalChatDraftScope(
  pathname: string,
  matches: readonly { params?: Record<string, unknown> }[],
): PersonalChatDraftScope | null {
  if (pathname === "/" || pathname === "/ask-puntt") return "home";

  for (let index = matches.length - 1; index >= 0; index -= 1) {
    const chatId = matches[index]?.params?.chatId;
    if (typeof chatId === "string" && chatId.length > 0) {
      return `chat:${chatId}`;
    }
  }
  return null;
}

function AskV2PersonalChatDraftOutlet() {
  const rootDraft = useAskV2AttachmentDraftRoot();
  const router = useRouter();
  const { pathname, matches, isLoading } = useRouterState({
    select: (state) => ({
      pathname: state.location.pathname,
      matches: state.matches,
      isLoading: state.isLoading,
    }),
  });
  const scope = personalChatDraftScope(
    withoutBasepath(pathname, router.basepath),
    matches,
  );
  const rootPreviousScope = rootDraft?.routeScopeRef.current ?? null;
  const pendingCreatedChatId =
    rootDraft?.pendingCreatedChatIdRef.current ?? null;
  const rootAdoptsNewChat =
    rootPreviousScope === "home" &&
    pendingCreatedChatId != null &&
    scope === `chat:${pendingCreatedChatId}`;
  const shouldResetRootDraft =
    rootDraft != null && rootPreviousScope !== scope && !rootAdoptsNewChat;

  useLayoutEffect(() => {
    if (!rootDraft || isLoading) return;
    if (shouldResetRootDraft) rootDraft.draft.clearAll();
    rootDraft.routeScopeRef.current = scope;
    if (scope !== "home") {
      rootDraft.pendingCreatedChatIdRef.current = null;
    }
  }, [isLoading, rootDraft, scope, shouldResetRootDraft]);

  if (scope == null) return <Outlet />;

  return (
    <AskV2AttachmentDraftProvider>
      <Outlet />
    </AskV2AttachmentDraftProvider>
  );
}

/**
 * Authenticated V2 chrome: left rail + page outlet. Isolated from
 * PunttLayout so tickets Documents never mount on ask.puntt.ai.
 *
 * The shared `AuthenticationDialog` IS mounted, even though the rest of
 * PunttLayout is not: V2's sidebar renders `<EnterpriseSwitcher/>`, and a
 * switch into an MFA-required enterprise parks its TOTP step in the store for
 * this dialog to render. Without the mount the switch silently does nothing.
 * It renders nothing while closed, so the V2 shell gains no chrome.
 */
export function AskV2Layout() {
  return (
    <div
      data-testid="ask-v2-shell"
      className="relative grid h-screen grid-cols-[auto_1fr] overflow-hidden bg-puntt-neutral-gray-2"
    >
      <AskPunttDesktopNotificationsWatcher />
      <AuthenticationDialog />
      <AskV2Sidebar />
      <div
        className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden"
        data-testid="ask-v2-outlet-shell"
      >
        <AskV2PersonalChatDraftOutlet />
      </div>
    </div>
  );
}
